import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, interval } from 'rxjs';
import { AuthService } from './auth.service';
import { BE_API_AUTH_URL } from '../constants/app.constants';

@Injectable({
  providedIn: 'root',
})
export class SessionTelemetryService {
  private readonly heartbeatMs = 60_000;
  private readonly inactivityMs = 5 * 60_000;
  private readonly excludedPrefixes = [
    '/login',
    '/auth-callback',
    '/reset-password',
    '/landing-page',
    '/company-landing-page',
    '/mobile-desktop',
    '/auth-link-error',
    '/accept-invite',
  ];

  private currentPath = '';
  private currentSessionKey: string | null = null;
  private lastActivityAt = Date.now();
  private listenersAttached = false;
  private heartbeatSub?: Subscription;

  constructor(private http: HttpClient, private authService: AuthService) {}

  initialize(): void {
    if (this.listenersAttached) {
      return;
    }

    this.listenersAttached = true;
    const markActive = () => {
      this.lastActivityAt = Date.now();
    };

    ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, markActive, { passive: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.lastActivityAt = Date.now();
        this.ensureSession();
      } else {
        this.flushSession('hidden');
      }
    });

    window.addEventListener('beforeunload', () => {
      this.flushSession('unload', true);
    });

    this.heartbeatSub = interval(this.heartbeatMs).subscribe(() => {
      if (!this.isTrackableRoute(this.currentPath) || !this.authService.isAuthenticated()) {
        this.endSession('route_excluded');
        return;
      }

      if (!this.currentSessionKey) {
        this.ensureSession();
        return;
      }

      if ((Date.now() - this.lastActivityAt) > this.inactivityMs) {
        this.endSession('inactive_timeout');
        return;
      }

      this.http.post(
        `${BE_API_AUTH_URL}/session-telemetry/heartbeat`,
        { sessionKey: this.currentSessionKey, path: this.currentPath },
        { headers: this.authService.createAuthorizationHeader() }
      ).subscribe({ error: () => undefined });
    });
  }

  handleRouteChange(url: string): void {
    this.currentPath = url || '';
    if (!this.authService.isAuthenticated() || !this.isTrackableRoute(this.currentPath)) {
      this.endSession('route_excluded');
      return;
    }
    this.ensureSession();
  }

  destroy(): void {
    this.endSession('service_destroyed');
    this.heartbeatSub?.unsubscribe();
  }

  private isTrackableRoute(url: string): boolean {
    return !!url && !this.excludedPrefixes.some((prefix) => url.startsWith(prefix));
  }

  private ensureSession(): void {
    if (!this.authService.isAuthenticated() || !this.isTrackableRoute(this.currentPath)) {
      return;
    }
    if (this.currentSessionKey) {
      return;
    }

    this.currentSessionKey = this.generateSessionKey();
    this.lastActivityAt = Date.now();
    this.http.post(
      `${BE_API_AUTH_URL}/session-telemetry/start`,
      { sessionKey: this.currentSessionKey, path: this.currentPath },
      { headers: this.authService.createAuthorizationHeader() }
    ).subscribe({
      error: () => {
        this.currentSessionKey = null;
      },
    });
  }

  private endSession(reason: string): void {
    if (!this.currentSessionKey || !this.authService.isAuthenticated()) {
      this.currentSessionKey = null;
      return;
    }

    const sessionKey = this.currentSessionKey;
    this.currentSessionKey = null;
    this.http.post(
      `${BE_API_AUTH_URL}/session-telemetry/end`,
      { sessionKey, path: this.currentPath, reason },
      { headers: this.authService.createAuthorizationHeader() }
    ).subscribe({ error: () => undefined });
  }

  private flushSession(reason: string, keepalive = false): void {
    if (!this.currentSessionKey || !this.authService.isAuthenticated()) {
      return;
    }

    if (!keepalive) {
      this.endSession(reason);
      return;
    }

    const sessionKey = this.currentSessionKey;
    this.currentSessionKey = null;
    const token = this.authService.getToken();
    if (!token) {
      return;
    }

    fetch(`${BE_API_AUTH_URL}/session-telemetry/end`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionKey,
        path: this.currentPath,
        reason,
      }),
    }).catch(() => undefined);
  }

  private generateSessionKey(): string {
    return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
